import { useState, useEffect } from 'react';
import { CreditCard } from 'lucide-react';
import { CalculatorCard, SliderInput } from '../CalculatorCard';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

export const LoanCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(200000);
  const [interestRate, setInterestRate] = useState(12);
  const [loanTerm, setLoanTerm] = useState(5);
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [totalPayment, setTotalPayment] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    const monthlyRate = interestRate / 100 / 12;
    const numPayments = loanTerm * 12;
    
    if (monthlyRate > 0) {
      const payment = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
                     (Math.pow(1 + monthlyRate, numPayments) - 1);
      
      setMonthlyPayment(Math.round(payment));
      setTotalPayment(Math.round(payment * numPayments));
      setTotalInterest(Math.round(payment * numPayments - loanAmount));
      
      const data = [];
      let remainingBalance = loanAmount;
      
      for (let year = 0; year <= loanTerm; year++) {
        if (year === 0) {
          data.push({ year: 0, zustatek: loanAmount });
        } else {
          for (let month = 0; month < 12 && remainingBalance > 0; month++) {
            const interestPayment = remainingBalance * monthlyRate;
            const principalPayment = Math.min(payment - interestPayment, remainingBalance);
            remainingBalance -= principalPayment;
          }
          data.push({ year: year, zustatek: Math.max(0, Math.round(remainingBalance)) });
        }
      }
      setChartData(data);
    }
  }, [loanAmount, interestRate, loanTerm]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-navy border border-white/10 rounded-lg p-3 shadow-lg">
          <p className="text-white font-semibold">{label}. rok</p>
          <p className="text-sm text-green">Zbývá: {payload[0]?.value?.toLocaleString('cs-CZ')} Kč</p>
        </div>
      );
    }
    return null;
  };

  const result = (
    <div className="space-y-6">
      <div className="text-right">
        <p className="text-gray-400 text-sm">Měsíční splátka</p>
        <p className="text-4xl font-bold text-green">{monthlyPayment.toLocaleString('cs-CZ')} Kč</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white/5 rounded-xl p-4">
          <p className="text-gray-400 text-xs">Celkem zaplatíte</p>
          <p className="text-white font-bold text-lg">{totalPayment.toLocaleString('cs-CZ')} Kč</p>
        </div>
        <div className="bg-white/5 rounded-xl p-4">
          <p className="text-gray-400 text-xs">Přeplatíte</p>
          <p className="text-gold font-bold text-lg">{totalInterest.toLocaleString('cs-CZ')} Kč</p>
        </div>
      </div>

      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="colorZustatek" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#22C55E" stopOpacity={0.4}/>
                <stop offset="95%" stopColor="#22C55E" stopOpacity={0.05}/>
              </linearGradient>
            </defs>
            <XAxis 
              dataKey="year" 
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={false}
            />
            <YAxis 
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={false}
              tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area 
              type="monotone" 
              dataKey="zustatek" 
              name="Zbývající dluh"
              stroke="#22C55E" 
              fill="url(#colorZustatek)" 
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  return (
    <CalculatorCard
      id="uver"
      title="Splátková kalkulačka"
      subtitle="Výpočet splátek spotřebitelského úvěru"
      icon={CreditCard}
      tip="Před podpisem úvěru vždy porovnejte RPSN (roční procentní sazba nákladů), ne jen úrok. RPSN zahrnuje všechny poplatky a je lepším ukazatelem skutečné ceny úvěru."
      result={result}
      showResult={true}
      testId="loan-calculator"
    >
      <SliderInput
        label="Výše úvěru"
        value={loanAmount}
        onChange={setLoanAmount}
        min={10000}
        max={1000000}
        step={10000}
        suffix="Kč"
        testId="loan-amount-slider"
      />
      <SliderInput
        label="Úroková sazba"
        value={interestRate}
        onChange={setInterestRate}
        min={3}
        max={30}
        step={0.5}
        suffix="%"
        formatValue={(v) => `${v.toFixed(1)} %`}
        testId="loan-rate-slider"
      />
      <SliderInput
        label="Doba splácení"
        value={loanTerm}
        onChange={setLoanTerm}
        min={1}
        max={10}
        step={1}
        suffix="let"
        testId="loan-term-slider"
      />
    </CalculatorCard>
  );
};
